import { onMounted, onUnmounted, nextTick } from 'vue';
import { useHeaderStore } from '@/stores/header';

/**
 * Apply the header's dark "alt" logo — but ONLY when this block is the first
 * block in the Builder block list (index 0), i.e. the block sitting directly
 * under the fixed header. A hero-styled block placed lower on the page (below
 * another hero — e.g. Sessions Archive beneath the dark Past Speakers carousel
 * on /2025-sessions) must NOT hijack the logo and stamp a dark mark onto the
 * preceding block's background.
 *
 * Outside a Builder block list (standalone Nuxt page routes such as the session
 * detail page) the block IS the page hero, so the alt logo always applies.
 *
 * Builder renders each block as a `.builder-block` wrapper inside a
 * `.builder-blocks` container, interleaved with `<style>` tags and a trailing
 * tracking-pixel `.builder-pixel` — so "index 0" is the first `.builder-block`
 * child that isn't the pixel.
 *
 * @param {import('vue').Ref<HTMLElement|null>} rootRef - ref on the block's root element
 */
export function useHeroAltLogo(rootRef) {
    const headerStore = useHeaderStore();
    let applied = false;

    const isFirstBlock = () => {
        const el = rootRef.value;
        if (!el) return false;
        const wrapper = el.parentElement;          // Builder per-block wrapper
        const container = wrapper?.parentElement;  // .builder-blocks
        // Not inside a Builder block list (standalone Nuxt page) → treat as hero.
        if (!container?.classList?.contains('builder-blocks')) return true;
        const firstBlock = [...container.children].find(
            (c) => c.classList.contains('builder-block') && !c.classList.contains('builder-pixel'),
        );
        return firstBlock === wrapper;
    };

    onMounted(async () => {
        await nextTick();
        if (isFirstBlock()) {
            headerStore.altLogo++;
            applied = true;
        }
    });

    onUnmounted(() => {
        if (applied) {
            headerStore.altLogo--;
            applied = false;
        }
    });
}
